// Customer Credit — Credit Ledger screen.
//
// Admin-only, routed at /credit-ledger/:customerId. Shows the customer's
// Credit Limit / current outstanding (creditBalance) / computed Available
// Credit as a summary header, the paginated transaction ledger below it
// (Opening Balance / Usage / Settlement rows), and a "Record Settlement"
// dialog. Mirrors customer.tsx's DataTable + offset/limit/page pagination
// pattern.
import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import { pdf } from "@react-pdf/renderer";
import { ColumnDef } from "@tanstack/react-table";
import { ArrowLeft, Receipt } from "lucide-react";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { Input } from "ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "ui/dialog";
import ApparelMenu from "../SubComponents/apparelMenu";
import ViewBillingSheet from "../orders/ViewBillingSheet";
import BillReceipt from "../billReceipt";
import {
    getCustomerCreditLedgerAction,
    recordCustomerCreditSettlementAction,
    getReferenceDataAction,
    getInvoiceDetailsAction,
} from "../../Store/actions/apparelAction";

const PAGE_LIMIT = 10;
// Repaying a credit debt with "Credit" itself makes no sense, and "Phonepe"
// is excluded here per business decision — neither is offered as a
// settlement method even though both are valid Payment Methods for billing.
const SETTLEMENT_EXCLUDED_PAYMENT_METHODS = ["Credit", "Phonepe"];
// referenceGroupId 4 = Payment Method (confirmed against apparelReducer.ts's
// API_GET_REFERENCE_DATA_SUCCESS split: group 4 -> paymentTypeData).
const PAYMENT_METHOD_REFERENCE_GROUP_ID = 4;

// Credit amounts are always shown as whole rupees, never decimals.
const formatRupees = (value: any) => Math.round(Number(value ?? 0)).toLocaleString("en-IN");

const CustomerCreditLedger = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const {
        customerCreditLedgerData,
        customerCreditLedgerOnload,
        recordCustomerCreditSettlementOnload,
        recordCustomerCreditSettlementData,
        paymentTypeData,
        getInvoiceDetailsOnload,
        getInvoiceDetailsData,
    } = apparelReducerState;
    const invoiceDetails = getInvoiceDetailsData?.getInvoiceDetails;

    const ledgerResult = customerCreditLedgerData?.getCustomerCreditLedger;
    const ledgerRows = ledgerResult?.creditLedger || [];
    const ledgerPage = ledgerResult?.page;
    const paymentList = paymentTypeData?.getReferenceData;

    // Arriving from Audit Log. Two distinct cases (see auditLog.tsx's
    // isCreditRelated): a real tbl_customercreditledger transaction
    // highlights that exact ledger row (see highlightCreditLedgerId below);
    // a customerDetail creditLimit change has no ledger row at all, so it
    // highlights the Credit Limit summary card instead.
    const highlightCreditLimitCard = !!location.state?.highlightCreditLimitCard;
    const creditLimitCardRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (highlightCreditLimitCard) creditLimitCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, [highlightCreditLimitCard]);

    const [offset, setOffset] = React.useState(0);
    // "" = All. Values match tbl_customercreditledger.direction (CREDIT/DEBIT).
    const [directionFilter, setDirectionFilter] = React.useState("");

    // The audit entry carries this exact ledger row's uniqueKey (see
    // BaseService.recordAudit's "<customerKey>|<ledgerKey>"), so the audited
    // transaction is highlighted directly — it used to highlight whichever
    // row happened to be first, which was only ever right for the newest one.
    const highlightCreditLedgerId: string | undefined = location.state?.highlightCreditLedgerId;

    const getLedgerApi = React.useCallback((nextOffset: number, direction: string, seekLedgerId?: string) => {
        dispatch(getCustomerCreditLedgerAction({
            customerId,
            direction: direction || undefined,
            // When set, the service ignores `offset` and returns whichever page
            // actually contains this row — the audited entry may be far down a
            // newest-first list.
            creditLedgerId: seekLedgerId,
            limit: PAGE_LIMIT,
            offset: nextOffset,
        }));
    }, [dispatch, customerId]);

    // Only the FIRST load honours highlightCreditLedgerId; once the user pages
    // or filters, the server must respect their offset again.
    const seekConsumed = React.useRef(false);

    React.useEffect(() => {
        const seekLedgerId = (!seekConsumed.current && highlightCreditLedgerId) ? highlightCreditLedgerId : undefined;
        seekConsumed.current = true;
        getLedgerApi(offset, directionFilter, seekLedgerId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, directionFilter, customerId]);

    React.useEffect(() => {
        dispatch(getReferenceDataAction({ referenceGroupId: PAYMENT_METHOD_REFERENCE_GROUP_ID }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The server may have landed us on a different page than `offset` says
    // (see the creditLedgerId seek above), so a page click that resolves to
    // the offset already in state still has to re-fetch.
    const onPageChange = (nextPage: number) => {
        const next = ((nextPage * PAGE_LIMIT) - PAGE_LIMIT === 0) ? 0 : (nextPage * PAGE_LIMIT) - PAGE_LIMIT;
        if (next === offset) {
            getLedgerApi(next, directionFilter);
            return;
        }
        setOffset(next);
    };

    const onDirectionFilterChange = (value: string) => {
        setDirectionFilter(value === "ALL" ? "" : value);
        setOffset(0);
    };

    // Usage row -> its billing invoice, viewed via the same sheet Billing
    // uses (see billing.tsx's getInvoiceDetailsApi/ViewBillingSheet pair).
    const [openViewBilling, setOpenViewBilling] = React.useState(false);
    const [viewInvoiceLoading, setViewInvoiceLoading] = React.useState(false);
    const [callGetInvoice, setCallGetInvoice] = React.useState(false);

    const viewInvoice = (row: any) => {
        dispatch(getInvoiceDetailsAction({ invoiceId: row?.invoiceId }));
        setViewInvoiceLoading(true);
        setOpenViewBilling(true);
    };

    React.useEffect(() => {
        if (viewInvoiceLoading && getInvoiceDetailsOnload === false) {
            setViewInvoiceLoading(false);
            setCallGetInvoice(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewInvoiceLoading, getInvoiceDetailsOnload]);

    const handlePrintInvoice = async () => {
        try {
            const blob = await pdf(<BillReceipt invoiceData={invoiceDetails} customerDetails={undefined} />).toBlob();
            const pdfUrl = URL.createObjectURL(blob);
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = pdfUrl;
            document.body.appendChild(iframe);
            iframe.onload = () => {
                setTimeout(() => iframe.contentWindow?.print(), 500);
            };
        } catch (error) {
            console.error("Error generating PDF:", error);
            toast.error("Failed to generate receipt. Please try again.");
        }
    };

    const [settleOpen, setSettleOpen] = React.useState(false);
    const [settleAmount, setSettleAmount] = React.useState("");
    const [settlePaymentMethodRefId, setSettlePaymentMethodRefId] = React.useState<string | undefined>(undefined);
    const [settleRemarks, setSettleRemarks] = React.useState("");
    const [settleSubmitted, setSettleSubmitted] = React.useState(false);

    const openSettle = () => {
        setSettleAmount("");
        setSettlePaymentMethodRefId(undefined);
        setSettleRemarks("");
        setSettleOpen(true);
    };

    const submitSettlement = () => {
        if (!settleAmount || Number(settleAmount) <= 0) {
            toast.error("Enter a valid settlement amount");
            return;
        }
        dispatch(recordCustomerCreditSettlementAction({
            customerId,
            amount: Number(settleAmount),
            paymentMethodRefId: settlePaymentMethodRefId ? Number(settlePaymentMethodRefId) : null,
            remarks: settleRemarks,
        }));
        setSettleSubmitted(true);
    };

    React.useEffect(() => {
        if (recordCustomerCreditSettlementOnload == false && settleSubmitted == true) {
            setSettleSubmitted(false);
            const result = recordCustomerCreditSettlementData?.data?.recordCustomerCreditSettlement;
            if (result === "Updated Successfully") {
                toast.success("Settlement recorded");
                setSettleOpen(false);
                getLedgerApi(offset, directionFilter);
            } else {
                toast.error(result || "Failed to record settlement");
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordCustomerCreditSettlementOnload, settleSubmitted]);

    const columns = React.useMemo<ColumnDef<any, any>[]>(() => [
        {
            id: "transactionDate",
            header: "Date",
            cell: ({ row }) => moment(row.original?.transactionDate).format("DD-MMM-YYYY"),
        },
        {
            id: "transactionTypeName",
            header: "Type",
            // "Usage" is the seeded reference name (tbl_referenceGroup
            // "Customer Credit Transaction Type") — displayed as "Purchase"
            // here without touching the stored value.
            cell: ({ row }) => (row.original?.transactionTypeName === "Usage" ? "Purchase" : row.original?.transactionTypeName) || "-",
        },
        {
            id: "direction",
            header: "Direction",
            cell: ({ row }) => (
                <Badge variant={row.original?.direction === "DEBIT" ? "destructive" : "success"}>
                    {row.original?.direction}
                </Badge>
            ),
        },
        {
            id: "amount",
            header: "Amount",
            cell: ({ row }) => <span className="tabular-nums">₹{formatRupees(row.original?.amount)}</span>,
        },
        {
            id: "newBalance",
            header: "Balance After",
            cell: ({ row }) => <span className="tabular-nums">₹{formatRupees(row.original?.newBalance)}</span>,
        },
        {
            id: "remarks",
            header: "Remarks",
            cell: ({ row }) => row.original?.remarks || "-",
        },
        {
            id: "invoice",
            header: "Invoice",
            // Only Usage rows carry a billingId (see tbl_customercreditledger's
            // billingId FK) — Opening Balance/Settlement rows have none.
            cell: ({ row }) => row.original?.invoiceId ? (
                <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={() => viewInvoice(row.original)}>
                    <Receipt className="h-3.5 w-3.5" />
                    {row.original.invoiceId}
                </Button>
            ) : "-",
        },
    ], []);

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="3" />
            <div className="mb-4 flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => navigate("/customer")}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-lg font-semibold text-foreground">
                    Customer Credit Ledger{ledgerResult?.customerName ? ` — ${ledgerResult.customerName}` : ""}
                </h1>
                {ledgerResult?.mobileNumber && (
                    <Badge variant="secondary" className="whitespace-nowrap">{ledgerResult.mobileNumber}</Badge>
                )}
                {ledgerResult?.customerCode && (
                    <Badge variant="secondary" className="whitespace-nowrap">{ledgerResult.customerCode}</Badge>
                )}
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div
                    ref={creditLimitCardRef}
                    className={`rounded-xl border p-4 ${highlightCreditLimitCard ? "bg-primary/10 ring-1 ring-inset ring-primary/40" : ""}`}
                >
                    <div className="text-xs text-muted-foreground">Credit Limit</div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">₹{formatRupees(ledgerResult?.creditLimit)}</div>
                </div>
                <div className="rounded-xl border p-4">
                    <div className="flex items-center gap-2">
                        <div className="text-xs text-muted-foreground">Outstanding Balance</div>
                        {ledgerResult?.openingBalanceAmount != null && (
                            <Badge variant="secondary" className="whitespace-nowrap">
                                Opening Balance: ₹{formatRupees(ledgerResult.openingBalanceAmount)}
                            </Badge>
                        )}
                    </div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">₹{formatRupees(ledgerResult?.creditBalance)}</div>
                </div>
                <div className="rounded-xl border p-4">
                    <div className="text-xs text-muted-foreground">Available Credit</div>
                    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">₹{formatRupees(ledgerResult?.availableCredit)}</div>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={ledgerRows}
                loading={customerCreditLedgerOnload}
                title="Transactions"
                headerActions={<Button onClick={openSettle}>Record Settlement</Button>}
                toolbar={
                    <Select value={directionFilter || "ALL"} onValueChange={onDirectionFilterChange}>
                        <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All</SelectItem>
                            <SelectItem value="CREDIT">Credit</SelectItem>
                            <SelectItem value="DEBIT">Debit</SelectItem>
                        </SelectContent>
                    </Select>
                }
                highlightRowKey={highlightCreditLedgerId}
                getRowKey={(row: any) => row?.creditLedgerId}
                pagination={{
                    page: ledgerPage?.currentPage || 1,
                    total: ledgerPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />

            <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Record Settlement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <Input
                            type="number"
                            min={0}
                            placeholder="Amount"
                            value={settleAmount}
                            onChange={(e) => setSettleAmount(e.target.value)}
                        />
                        <Select value={settlePaymentMethodRefId} onValueChange={setSettlePaymentMethodRefId}>
                            <SelectTrigger><SelectValue placeholder="Payment Method" /></SelectTrigger>
                            <SelectContent>
                                {(paymentList || [])
                                    .filter((item: any) => !SETTLEMENT_EXCLUDED_PAYMENT_METHODS.includes(item?.name))
                                    .map((item: any) => (
                                        <SelectItem key={item?.id} value={String(item?.id)}>{item?.name}</SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                        <Input
                            placeholder="Remarks (optional)"
                            value={settleRemarks}
                            onChange={(e) => setSettleRemarks(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
                        <Button disabled={recordCustomerCreditSettlementOnload} onClick={submitSettlement}>Record</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ViewBillingSheet
                open={openViewBilling}
                onClose={() => setOpenViewBilling(false)}
                customerDetails={undefined}
                invoiceDetails={invoiceDetails}
                callGetInvoice={callGetInvoice}
                onPrint={handlePrintInvoice}
            />
        </div>
    );
};

export default CustomerCreditLedger;
